import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Database, Rss, GitCompareArrows, CalendarClock, AlertTriangle } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Card } from '../components/ui/card';
import RunPipelineButton from '../components/RunPipelineButton';
import usePollWhileActive from '../hooks/usePollWhileActive';
import { StatusBadge, fmtDate, fmtNum, fmtDur } from '../components/feeds/shared';
import { api } from '../api/feedy';

// Elapsed seconds between two timestamps (for fmtDur).
const durationS = (start, end) => {
  if (!start) return null;
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, (b - a) / 1000);
};

const TRIGGER_LABEL = { schedule: 'Scheduled', manual: 'Manual', api: 'API' };

// "7/7 sources · 41/41 feeds · 3 failed"
function summaryLine(s) {
  if (!s) return null;
  const parts = [];
  if (s.sources_total != null) parts.push(`${fmtNum(s.sources_ok)}/${fmtNum(s.sources_total)} sources`);
  if (s.feeds_total != null) parts.push(`${fmtNum(s.feeds_ok)}/${fmtNum(s.feeds_total)} feeds`);
  if (s.compares_total) parts.push(`${fmtNum(s.compares_ok)}/${fmtNum(s.compares_total)} compares`);
  const failed = (s.sources_failed || 0) + (s.feeds_failed || 0) + (s.compares_failed || 0);
  return { text: parts.join(' · '), failed };
}

const KIND_META = {
  source: { icon: Database, label: 'Sources' },
  feed: { icon: Rss, label: 'Feeds' },
  compare: { icon: GitCompareArrows, label: 'Comparisons' },
};

function StepGroup({ kind, steps }) {
  const meta = KIND_META[kind] || { icon: Rss, label: kind };
  const Icon = meta.icon;
  const failed = steps.filter((s) => s.status === 'error').length;
  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[12px] font-semibold">{meta.label}</span>
        <span className="font-mono text-[11px] text-muted-foreground tnum">{steps.length}</span>
        {failed > 0 && (
          <span className="ml-auto rounded bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-rose-600">
            {failed} failed
          </span>
        )}
      </div>
      <div className="divide-y divide-border/70">
        {steps.map((st, i) => {
          const isErr = st.status === 'error';
          return (
            <div key={st.id ?? i} className={`flex items-center gap-3 px-3 py-1.5 ${isErr ? 'bg-rose-500/5' : ''}`}>
              <div className="w-20 shrink-0"><StatusBadge status={st.status} /></div>
              <span className={`min-w-0 flex-1 truncate text-[12px] ${isErr ? 'font-medium text-rose-700' : ''}`}>{st.name}</span>
              {st.detail && (
                <span className={`hidden max-w-[45%] truncate font-mono text-[11px] sm:block ${isErr ? 'text-rose-600' : 'text-muted-foreground'}`} title={st.detail}>
                  {st.detail}
                </span>
              )}
              <span className="tnum w-16 shrink-0 text-right font-mono text-[11px] text-muted-foreground">
                {st.ms != null ? fmtDur(st.ms / 1000) : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RunRow({ run }) {
  const [open, setOpen] = useState(false);
  const sum = summaryLine(run.summary);
  const dur = durationS(run.started_at, run.finished_at);
  const grouped = useMemo(() => {
    const m = { source: [], feed: [], compare: [] };
    for (const st of (Array.isArray(run.steps) ? run.steps : [])) (m[st.kind] ??= []).push(st);
    return m;
  }, [run.steps]);
  const hasSteps = (run.steps?.length || 0) > 0;

  return (
    <div>
      <button
        onClick={() => hasSteps && setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-muted/40 disabled:cursor-default"
        disabled={!hasSteps}
      >
        <span className="w-4 shrink-0 text-muted-foreground">
          {hasSteps ? (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : null}
        </span>
        <div className="w-20 shrink-0"><StatusBadge status={run.status} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{TRIGGER_LABEL[run.trigger] || run.trigger || 'Run'}</span>
            <span className="font-mono text-[11px] text-muted-foreground tnum">{fmtDate(run.started_at)}</span>
          </div>
          {sum?.text && (
            <p className="truncate font-mono text-[11px] text-muted-foreground tnum">
              {sum.text}
              {sum.failed > 0 && <span className="text-rose-600"> · {sum.failed} failed</span>}
            </p>
          )}
        </div>
        <span className="tnum hidden w-20 shrink-0 text-right font-mono text-[12px] text-muted-foreground sm:block">
          {dur != null ? fmtDur(dur) : '—'}
        </span>
      </button>

      {open && hasSteps && (
        <div className="space-y-3 border-t border-border bg-muted/20 px-5 py-3">
          {run.error_message && (
            <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-500/5 px-3 py-2 text-[12px] text-rose-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="break-words">{run.error_message}</span>
            </div>
          )}
          {['source', 'feed', 'compare'].map((kind) => (
            grouped[kind]?.length ? <StepGroup key={kind} kind={kind} steps={grouped[kind]} /> : null
          ))}
        </div>
      )}
    </div>
  );
}

export default function Schedule() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = (soft = false) => {
    if (!soft) setLoading(true);
    return api.pipelineRuns.list()
      .then(setRuns)
      .catch(() => {})
      .finally(() => { if (!soft) setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const running = useMemo(() => runs[0]?.status === 'running', [runs]);
  usePollWhileActive(running, () => load(true));

  return (
    <AppLayout
      title="Runs"
      description="Pipeline run history — daily sync &amp; generate"
      actions={<RunPipelineButton running={running} onStarted={() => load(true)} />}
    >
      <Card>
        <div className="flex items-center gap-2 border-b border-border px-5 py-3">
          <CalendarClock className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Recent runs</h2>
          <span className="font-mono text-[11px] text-muted-foreground tnum">{runs.length}</span>
          {running && (
            <span className="ml-1 inline-flex items-center gap-1.5 font-mono text-[11px] text-amber-600" title="A run is in progress — updating automatically">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> live
            </span>
          )}
        </div>
        <div className="divide-y divide-border">
          {loading && <p className="px-5 py-10 text-center text-sm text-muted-foreground">Loading…</p>}
          {!loading && runs.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-muted-foreground">No runs yet. Hit “Run pipeline” to start one.</p>
          )}
          {!loading && runs.map((r) => <RunRow key={r.id} run={r} />)}
        </div>
      </Card>
    </AppLayout>
  );
}
